import { ScoreBoardVariant } from '@/components/scoreboard/types';
import { normalizeSportName } from '@/utils/get-sport-key';

export type TeamSide = 'home' | 'away';
export type TeamOrder = [TeamSide, TeamSide];
export type ScoreboardSurface = 'hud' | 'card';
export type SectionTitleVariant = 'games' | 'matchups';

type SportConfig = {
    teamOrder: TeamOrder;
    separatorSymbol: string;
    teamNameDisplay: 'alias' | 'short_code';
    sectionTitles: SectionTitleVariant;
    showLeagueName?: boolean;
    /** Render the live HUD as the scoreboard alone (no HUD-Live + Box-Score carousel). */
    standaloneScoreboard?: boolean;
};

const CLASSIC_VARIANTS = { hud: 'classicLarge', card: 'classicMedium' } as const;
const CLASSIC_CONDENSED_VARIANTS = { hud: 'classicCondensedLarge', card: 'classicCondensedMedium' } as const;

// Single source of truth for which sports render a ScoreBoardGrid. `SCOREBOARD_VARIANTS` (below)
// and `SPORT_BUILDERS` (use-scoreboard.ts) are both keyed by `ScoreboardSport`, so adding a sport to
// one without the other — or a sport carrying a variant with no builder — is a compile error. The
// data (variant) lives here in the pure layer; the behavior (builder, which imports components) stays
// in use-scoreboard.ts. Keys match `getSportKey`/`normalizeSportName` output (lowercase, _ for space).
export const SCOREBOARD_SPORTS = [
    'tennis',
    'baseball',
    'american_football',
    'basketball',
    'ice_hockey',
    'soccer',
] as const;
export type ScoreboardSport = (typeof SCOREBOARD_SPORTS)[number];

const SCOREBOARD_VARIANTS: Record<ScoreboardSport, Record<ScoreboardSurface, ScoreBoardVariant>> = {
    tennis: { hud: 'modernLarge', card: 'modernMedium' },
    baseball: CLASSIC_CONDENSED_VARIANTS,
    american_football: CLASSIC_VARIANTS,
    basketball: CLASSIC_VARIANTS,
    ice_hockey: CLASSIC_VARIANTS,
    soccer: CLASSIC_VARIANTS,
};

export const isScoreboardSport = (sportKey: string): sportKey is ScoreboardSport =>
    (SCOREBOARD_SPORTS as readonly string[]).includes(sportKey);

const FIGHT_CONFIG: SportConfig = {
    teamOrder: ['away', 'home'],
    separatorSymbol: 'vs',
    teamNameDisplay: 'short_code',
    sectionTitles: 'matchups',
};

const SPORT_CONFIG: Record<string, SportConfig> = {
    soccer: {
        teamOrder: ['home', 'away'],
        separatorSymbol: 'vs',
        teamNameDisplay: 'alias',
        sectionTitles: 'matchups',
    },
    tennis: {
        teamOrder: ['away', 'home'],
        separatorSymbol: 'vs',
        teamNameDisplay: 'short_code',
        sectionTitles: 'matchups',
        showLeagueName: true,
        standaloneScoreboard: true,
    },
    mma: FIGHT_CONFIG,
    boxing: FIGHT_CONFIG,
    american_football: {
        teamOrder: ['away', 'home'],
        separatorSymbol: '@',
        teamNameDisplay: 'short_code',
        sectionTitles: 'games',
    },
    basketball: {
        teamOrder: ['away', 'home'],
        separatorSymbol: '@',
        teamNameDisplay: 'short_code',
        sectionTitles: 'games',
    },
    ice_hockey: {
        teamOrder: ['away', 'home'],
        separatorSymbol: '@',
        teamNameDisplay: 'short_code',
        sectionTitles: 'games',
    },
    baseball: {
        teamOrder: ['away', 'home'],
        separatorSymbol: '@',
        teamNameDisplay: 'short_code',
        sectionTitles: 'games',
    },
};

const DEFAULT_CONFIG: SportConfig = {
    teamOrder: ['away', 'home'],
    separatorSymbol: '@',
    teamNameDisplay: 'short_code',
    sectionTitles: 'games',
};

const getSportConfig = (sportName: string): SportConfig => {
    return SPORT_CONFIG[normalizeSportName(sportName)] ?? DEFAULT_CONFIG;
};

// Returns the display order of teams based on sport (e.g., Soccer shows home first, others show away first)
export const getTeamOrder = (sportName: string): TeamOrder => {
    return getSportConfig(sportName).teamOrder;
};

// Returns the separator symbol between team names (e.g., "vs" for Soccer/Tennis, "@" for others)
export const getSeparatorSymbol = (sportName: string): string => {
    return getSportConfig(sportName).separatorSymbol;
};

export const getScoreboardVariants = (sportName: string): Record<ScoreboardSurface, ScoreBoardVariant> | undefined => {
    const sportKey = normalizeSportName(sportName);
    return isScoreboardSport(sportKey) ? SCOREBOARD_VARIANTS[sportKey] : undefined;
};

/**
 * Whether a sport's live HUD renders the scoreboard alone (no HUD-Live carousel slide).
 * An explicit per-sport switch — kept separate from the visual variant so a sport can pick
 * its scoreboard look (modern/classic) independently of whether it shows the carousel.
 */
export const isStandaloneScoreboardSport = (sportName: string): boolean => {
    return !!getSportConfig(sportName).standaloneScoreboard;
};

export const getSectionTitleVariant = (sportName: string): SectionTitleVariant => {
    return getSportConfig(sportName).sectionTitles;
};

// Returns whether the league name should be shown as a subtitle/header (e.g., tournament name for Tennis)
export const shouldShowLeagueName = (sportName: string): boolean => {
    return !!getSportConfig(sportName).showLeagueName;
};

// Returns the display name for a team based on sport (e.g., Soccer uses full name, others use short code)
export const getTeamDisplayName = (
    team: { alias?: string; name?: string; short_code: string },
    sportName: string
): string => {
    return getSportConfig(sportName).teamNameDisplay === 'alias'
        ? team.alias || team.name || team.short_code
        : team.short_code;
};

// Formats a matchup string using short_code with sport-specific order and separator (e.g., "WHU vs WOL" for Soccer, "MIA @ LAL" for Basketball)
export const formatMatchupString = (homeShortCode: string, awayShortCode: string, sportName: string): string => {
    const [firstSide, secondSide] = getTeamOrder(sportName);
    const separator = getSeparatorSymbol(sportName);
    const codes = { home: homeShortCode, away: awayShortCode };
    return `${codes[firstSide]} ${separator} ${codes[secondSide]}`;
};

// Formats a scored matchup string using short_code with sport-specific order and separator (e.g., "WHU 2 vs WOL 1" for Soccer)
export const formatScoredMatchupString = (
    homeShortCode: string,
    awayShortCode: string,
    homeScore: number | string | undefined,
    awayScore: number | string | undefined,
    sportName: string
): string => {
    const [firstSide, secondSide] = getTeamOrder(sportName);
    const separator = getSeparatorSymbol(sportName);
    const codes = { home: homeShortCode, away: awayShortCode };
    const scores = { home: homeScore, away: awayScore };

    const firstDetails = [codes[firstSide], scores[firstSide]].filter(v => v !== undefined && v !== '').join(' ');
    const secondDetails = [codes[secondSide], scores[secondSide]].filter(v => v !== undefined && v !== '').join(' ');

    return `${firstDetails} ${separator} ${secondDetails}`;
};
